import { apiClient, getStorageUrl, STORAGE_BASE_URL } from "../api/AllApi";

export const SYSTEM_CONFIG_STORAGE_KEY = "velan_system_config";

const SETTINGS_ENDPOINT = "/settings/system";

const normalizeCurrencyCode = (value, fallback = "") => {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (!normalized) return fallback;
  if (normalized === "$") return "USD";
  return normalized;
};

const normalizeLogoUrl = (value) => {
  if (!value) return "";
  let stringValue = String(value).trim().replace(/\\/g, "/");
  if (!stringValue) return "";
  if (stringValue.startsWith("http://") || stringValue.startsWith("https://") || stringValue.startsWith("data:")) {
    return stringValue;
  }
  if (stringValue.startsWith("storage/")) {
    stringValue = stringValue.slice("storage/".length);
  }
  return getStorageUrl(stringValue) || "";
};

/** If Laravel APP_URL omitted /api, API may return …/storage/… instead of …/api/storage/… — fix same-origin URLs. */
const normalizeAbsoluteStorageUrl = (explicit) => {
  try {
    const u = new URL(explicit);
    const apiRoot = String(STORAGE_BASE_URL || "").replace(/\/$/, "");
    if (!apiRoot.endsWith("/api")) return explicit;
    const apiOrigin = new URL(apiRoot).origin;
    if (u.origin !== apiOrigin || !u.pathname.startsWith("/storage/")) return explicit;
    const rest = u.pathname.replace(/^\/storage\/?/, "");
    return `${apiRoot}/storage/${rest}`;
  } catch {
    return explicit;
  }
};

/** Prefer absolute URLs from the API (correct APP_URL); else build from stored path */
const resolveMediaUrl = (storedPath, apiAbsoluteUrl) => {
  const explicit = String(apiAbsoluteUrl ?? "").trim();
  if (explicit) {
    if (/^https?:\/\//i.test(explicit)) {
      return normalizeAbsoluteStorageUrl(explicit);
    }
    if (explicit.startsWith("/")) {
      const base = String(STORAGE_BASE_URL || "").replace(/\/$/, "");
      return base ? `${base}${explicit}` : explicit;
    }
  }
  return normalizeLogoUrl(storedPath);
};

export const normalizeSystemConfig = (rawConfig = {}) => {
  const siteName = String(rawConfig.site_name || rawConfig.siteName || "Velan").trim() || "Velan";
  const loginLogo = rawConfig.login_logo || rawConfig.loginLogo || "";
  const sidebarLogo = rawConfig.sidebar_logo || rawConfig.sidebarLogo || "";
  const currencyCode = normalizeCurrencyCode(rawConfig.currency_code || rawConfig.currencyCode, "IQD");
  const secondaryRaw = rawConfig.secondary_currency_code ?? rawConfig.secondaryCurrencyCode ?? "";
  const secondaryCurrencyCode = normalizeCurrencyCode(secondaryRaw, "");
  const currencyDisplay = String(rawConfig.currency_display || rawConfig.currencyDisplay || "code").trim() || "code";
  const locale = String(rawConfig.locale || "en-US").trim() || "en-US";
  const fractionDigitsRaw = rawConfig.fraction_digits ?? rawConfig.fractionDigits;
  const fractionDigits = Number.isFinite(Number(fractionDigitsRaw)) ? Number(fractionDigitsRaw) : (currencyCode === "IQD" ? 0 : 2);

  const invoiceLogo = rawConfig.invoice_logo || rawConfig.invoiceLogo || "";
  const invoiceStamp = rawConfig.invoice_stamp_image || rawConfig.invoiceStampImage || "";
  const wmOpacityRaw = rawConfig.invoice_watermark_opacity ?? rawConfig.invoiceWatermarkOpacity;
  const wmOpacity = Number.isFinite(Number(wmOpacityRaw))
    ? Math.min(0.5, Math.max(0.02, Number(wmOpacityRaw)))
    : 0.07;

  return {
    site_name: siteName,
    login_logo: loginLogo,
    sidebar_logo: sidebarLogo,
    login_logo_url: resolveMediaUrl(loginLogo, rawConfig.login_logo_url ?? rawConfig.loginLogoUrl),
    sidebar_logo_url: resolveMediaUrl(sidebarLogo, rawConfig.sidebar_logo_url ?? rawConfig.sidebarLogoUrl),
    currency_code: currencyCode,
    secondary_currency_code: secondaryCurrencyCode,
    currency_display: currencyDisplay,
    locale,
    fraction_digits: Math.max(0, Math.min(6, fractionDigits)),

    invoice_company_name: String(rawConfig.invoice_company_name || "").trim(),
    invoice_tagline: String(rawConfig.invoice_tagline || "").trim(),
    invoice_logo: invoiceLogo,
    invoice_logo_url: resolveMediaUrl(invoiceLogo, rawConfig.invoice_logo_url ?? rawConfig.invoiceLogoUrl),
    invoice_address: String(rawConfig.invoice_address || "").trim(),
    invoice_phones: String(rawConfig.invoice_phones || "").trim(),
    invoice_email: String(rawConfig.invoice_email || "").trim(),
    invoice_website: String(rawConfig.invoice_website || "").trim(),
    invoice_footer_note: String(rawConfig.invoice_footer_note || "").trim(),
    invoice_watermark_enabled:
      rawConfig.invoice_watermark_enabled === undefined || rawConfig.invoice_watermark_enabled === null
        ? true
        : Boolean(rawConfig.invoice_watermark_enabled),
    invoice_watermark_opacity: wmOpacity,
    invoice_stamp_image: invoiceStamp,
    invoice_stamp_image_url: resolveMediaUrl(
      invoiceStamp,
      rawConfig.invoice_stamp_image_url ?? rawConfig.invoiceStampImageUrl
    ),
  };
};

export const readCachedSystemConfig = () => {
  try {
    const raw = localStorage.getItem(SYSTEM_CONFIG_STORAGE_KEY);
    if (!raw) return null;
    return normalizeSystemConfig(JSON.parse(raw));
  } catch (error) {
    return null;
  }
};

export const cacheSystemConfig = (config) => {
  const normalized = normalizeSystemConfig(config);
  localStorage.setItem(SYSTEM_CONFIG_STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
};

export const fetchSystemConfig = async () => {
  const res = await apiClient.get(SETTINGS_ENDPOINT, { skipAuthRedirect: true });
  return cacheSystemConfig(res?.data || {});
};

export const saveSystemConfig = async (payload) => {
  const hasFileUpload =
    payload.login_logo instanceof File ||
    payload.sidebar_logo instanceof File ||
    payload.invoice_logo instanceof File ||
    payload.invoice_stamp_image instanceof File;
  if (hasFileUpload) {
    const formData = new FormData();
    const allowEmptyFormKeys = new Set([
      "invoice_company_name",
      "invoice_tagline",
      "invoice_address",
      "invoice_phones",
      "invoice_email",
      "invoice_website",
      "invoice_footer_note",
      "invoice_logo_url",
      "invoice_stamp_image_url",
    ]);
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined) return;
      if (key === "secondary_currency_code" && (value === null || value === "")) {
        formData.append(key, "");
        return;
      }
      if (allowEmptyFormKeys.has(key) && value === "") {
        formData.append(key, "");
        return;
      }
      if (value === null || value === "") return;
      // Laravel's `boolean` rule only accepts true/false/0/1 — not FormData's "true"/"false" strings
      if (typeof value === "boolean") {
        formData.append(key, value ? "1" : "0");
        return;
      }
      formData.append(key, value);
    });
    const res = await apiClient.post(`${SETTINGS_ENDPOINT}?_method=PUT`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return cacheSystemConfig(res?.data || {});
  }

  const res = await apiClient.put(SETTINGS_ENDPOINT, payload);
  return cacheSystemConfig(res?.data || {});
};
